/**
 * Bokja account drawer: the "Sign in" row.
 *
 * James, 2026-08-13, on preview 188315402568: the row must open Shopify's on-storefront
 * sign-in sheet instead of navigating to /account, which bounces the customer off bokja.com
 * to the hosted shopify.com page (this store is on new customer accounts, and that page
 * sends `frame-ancestors 'none'`, so it cannot be brought back in an iframe either).
 *
 * HOW THE SHEET IS OPENED, AND WHY THERE IS NO CALL TO OPEN IT. `shopify-account` documents
 * two attributes (`menu`, `sign-in-url`), two events (`open`, `close`), one slot
 * (`signed-out-avatar`) and one CSS part (`signed-out-avatar`) — and NO method, property or
 * attribute that opens the sheet from script:
 *   https://shopify.dev/docs/api/storefront-web-components/components/shopify-account
 * The one documented way in is a customer clicking the avatar, and the avatar's markup can be
 * "replaced entirely using the slot":
 *   https://shopify.dev/docs/storefronts/themes/customer-engagement/account-component
 * So the row IS the avatar. The drawer slots its own <a> into `signed-out-avatar`; the click
 * lands on the component's own trigger through the flattened tree, and the component opens its
 * own sheet. Nothing here reaches into the shadow root, calls a private API, or fakes a click
 * on a hidden element — the alternative shapes, all of which were rejected.
 *
 * WHICH LEAVES THIS FILE ONE JOB: suppressing the anchor's navigation. The <a> has to keep a
 * real `href` (see FALLBACK), so when the component is live its default action would race the
 * sheet and win. `preventDefault()` stops the navigation only; it does not stop propagation,
 * so the component's own listener still runs.
 *
 * FALLBACK, in the shape bokja-account-collective.js argues for: an anchor whose default action
 * survives JS failure. If the bundle never arrives, if the element never upgrades, if this
 * module 404s or throws — the row is still `<a href="/account">` and still reaches the hosted
 * flow. So the guard below is positive, not negative: navigation is cancelled ONLY once the
 * element is proven upgraded (`instanceof` the registered constructor, not merely a tag match).
 * Every failure mode falls out of the `return` above it with the link intact. The collective
 * row hides itself when its dialog is missing because a plain link to the footer newsletter
 * would be the WRONG flow; here the plain link is the RIGHT flow, one hop further out, so this
 * row degrades instead of hiding.
 *
 * NO CHOREOGRAPHY HERE AT ALL, deliberately — see the note in bokja-account-drawer.liquid. The
 * collective row chooses its order: it calls `showModal()` itself and, since 2026-08-14, leaves
 * the drawer open so the invite stacks over it as a second panel. This row never calls anything:
 * the component opens the sheet synchronously, off its own trigger, before any code of ours could
 * run. There is no documented hook to defer it and no method to open it later, so the ordering the
 * collective row has is not available to this one. The outcome now matches anyway — both open over
 * the drawer — but one is a decision and this one is a constraint.
 */

const ROW = '[data-bokja-account-signin]';

document.addEventListener('click', (event) => {
  const target = /** @type {Element | null} */ (event.target);
  const row = target?.closest?.(ROW);
  if (!row) return;

  const host = row.closest('shopify-account');
  if (!host) return;

  const Registered = customElements.get('shopify-account');
  if (typeof Registered !== 'function' || !(host instanceof Registered)) return;

  event.preventDefault();
});
